/**
 * Curated GMAT prep resources — ranked by quality and student intent.
 * Deep-link only (no re-hosting). Rotate daily picks via content-daily.ts.
 */

export type ContentTag =
  | "quant"
  | "verbal"
  | "di"
  | "cr"
  | "rc"
  | "ds"
  | "admissions"
  | "strategy"
  | "mindset";

export interface YouTubeChannel {
  id: string;
  name: string;
  bestFor: string;
  why: string;
  url: string;
  tags: ContentTag[];
}

export interface CuratedVideo {
  id: string;
  /** YouTube video id (11 chars) — used for iframe embed + thumbnail. */
  youtubeId: string;
  title: string;
  channel: string;
  channelUrl: string;
  /** Watch URL; defaults derived from youtubeId if omitted. */
  url: string;
  duration?: string;
  tag: string;
  tags: ContentTag[];
}

export interface CuratedArticle {
  id: string;
  title: string;
  source: string;
  url: string;
  minutes: number;
  tag: string;
  tags: ContentTag[];
}

export interface CuratedLink {
  id: string;
  name: string;
  host: string;
  url: string;
  description: string;
  category: "official" | "forum" | "admissions" | "prep" | "social";
  tags: ContentTag[];
}

export interface CommunityThread {
  id: string;
  source: string;
  title: string;
  url: string;
  replies?: number;
  tags: ContentTag[];
}

export interface SocialAccount {
  id: string;
  platform: "instagram" | "linkedin" | "facebook";
  handle: string;
  followers?: string;
  description: string;
  url: string;
}

export interface PodcastShow {
  id: string;
  name: string;
  publisher: string;
  description: string;
  url: string;
}

export const YOUTUBE_CHANNELS: YouTubeChannel[] = [
  {
    id: "gmat-ninja",
    name: "GMAT Ninja",
    bestFor: "Verbal (CR + RC)",
    why: "Gold standard for CR/RC explanations; legendary in the GMAT Club community.",
    url: "https://www.youtube.com/@GMATNinjaTutoring",
    tags: ["verbal", "cr", "rc"],
  },
  {
    id: "gmat-club",
    name: "GMAT Club",
    bestFor: "Admissions + community",
    why: "Daily prep videos, admissions interviews, and advice from 1.5M+ members.",
    url: "https://www.youtube.com/@GMATClub",
    tags: ["strategy", "admissions"],
  },
  {
    id: "magoosh",
    name: "Magoosh GMAT",
    bestFor: "Beginner-friendly full course",
    why: "Comprehensive Quant, Verbal, and DI lessons; weekly uploads.",
    url: "https://www.youtube.com/@magooshgmat",
    tags: ["quant", "verbal", "di", "strategy"],
  },
  {
    id: "manhattan",
    name: "Manhattan Prep GMAT",
    bestFor: "Deep strategy + free mini-courses",
    why: "~195 videos from 5-min spotlights to full hour-long skill sessions.",
    url: "https://www.youtube.com/@ManhattanPrep",
    tags: ["quant", "verbal", "strategy"],
  },
  {
    id: "ttp",
    name: "Target Test Prep",
    bestFor: "Quant depth",
    why: "Closest to official GMAT difficulty; strong for 700+ Quant goals.",
    url: "https://www.youtube.com/@TargetTestPrep",
    tags: ["quant"],
  },
  {
    id: "egmat",
    name: "e-GMAT",
    bestFor: "Verbal + DI (non-native speakers)",
    why: "Most-reviewed prep company on GMAT Club; active since 2010.",
    url: "https://www.youtube.com/@eGMAT",
    tags: ["verbal", "di"],
  },
  {
    id: "crackverbal",
    name: "CrackVerbal",
    bestFor: "MBA admissions + GMAT",
    why: "30,000+ students; strong hybrid admissions + test prep content.",
    url: "https://www.youtube.com/@CrackVerbal",
    tags: ["admissions", "strategy"],
  },
  {
    id: "official-gmac",
    name: "The Official GMAT (GMAC)",
    bestFor: "Format & policy",
    why: "Canonical source for Focus Edition format, scoring, and GMAC updates.",
    url: "https://www.youtube.com/@TheOfficialGMAT",
    tags: ["strategy"],
  },
  {
    id: "gmatprepnow",
    name: "GMAT Prep Now",
    bestFor: "Free complete course",
    why: "Brent Hanneson — clear, no-fluff explanations beloved on r/GMAT.",
    url: "https://www.youtube.com/@GMATPrepNow",
    tags: ["quant", "verbal"],
  },
];

export const CURATED_VIDEOS: CuratedVideo[] = [
  {
    id: "v-cr-negation",
    youtubeId: "mR346Xb8JPs",
    title: "How to approach GMAT Focus Critical Reasoning",
    channel: "GMAT Ninja",
    channelUrl: "https://www.youtube.com/@GMATNinjaTutoring",
    url: "https://www.youtube.com/watch?v=mR346Xb8JPs",
    duration: "18:00",
    tag: "Verbal",
    tags: ["verbal", "cr"],
  },
  {
    id: "v-ds-trap",
    youtubeId: "G7byje8BBfs",
    title: "Data Sufficiency — rephrasing & testing cases",
    channel: "Manhattan Prep",
    channelUrl: "https://www.youtube.com/@ManhattanPrep",
    url: "https://www.youtube.com/watch?v=G7byje8BBfs",
    duration: "60:00",
    tag: "DI",
    tags: ["di", "ds"],
  },
  {
    id: "v-quant-fundamentals",
    youtubeId: "rB0i0xpmG_A",
    title: "Advanced quant strategies for a Q51",
    channel: "Target Test Prep",
    channelUrl: "https://www.youtube.com/@TargetTestPrep",
    url: "https://www.youtube.com/watch?v=rB0i0xpmG_A",
    tag: "Quant",
    tags: ["quant"],
  },
  {
    id: "v-rc-main",
    youtubeId: "SqWSPllwYJI",
    title: "Reading comprehension strategies that save time",
    channel: "Magoosh GMAT",
    channelUrl: "https://www.youtube.com/@magooshgmat",
    url: "https://www.youtube.com/watch?v=SqWSPllwYJI",
    tag: "Verbal",
    tags: ["verbal", "rc"],
  },
  {
    id: "v-msr",
    youtubeId: "-Ayc-MpRkpk",
    title: "Intro to multi-source reasoning",
    channel: "GMAT Ninja",
    channelUrl: "https://www.youtube.com/@GMATNinjaTutoring",
    url: "https://www.youtube.com/watch?v=-Ayc-MpRkpk",
    tag: "DI",
    tags: ["di"],
  },
  {
    id: "v-focus-edition",
    youtubeId: "477qt9LiSrc",
    title: "GMAT Focus Edition explained in 5 minutes",
    channel: "GMAC / prep community",
    channelUrl: "https://www.youtube.com/@TheOfficialGMAT",
    url: "https://www.youtube.com/watch?v=477qt9LiSrc",
    duration: "5:00",
    tag: "Strategy",
    tags: ["strategy"],
  },
];

export const CURATED_ARTICLES: CuratedArticle[] = [
  {
    id: "a-poq-scores",
    title: "What MBA admissions really look at in a 705+ score",
    source: "Poets & Quants",
    url: "https://poetsandquants.com/",
    minutes: 6,
    tag: "Admissions",
    tags: ["admissions"],
  },
  {
    id: "a-ttp-schools",
    title: "Average GMAT scores for top US business schools",
    source: "Target Test Prep Blog",
    url: "https://targettestprep.com/blog/",
    minutes: 8,
    tag: "Strategy",
    tags: ["admissions", "strategy"],
  },
  {
    id: "a-magoosh-plan",
    title: "How to build a 90-day GMAT study plan that actually works",
    source: "Magoosh GMAT Blog",
    url: "https://magoosh.com/gmat/",
    minutes: 5,
    tag: "Strategy",
    tags: ["strategy"],
  },
  {
    id: "a-egmat-essays",
    title: "2025–2026 essay guides for Kellogg, Wharton, and top programs",
    source: "e-GMAT Blog",
    url: "https://e-gmat.com/blogs/",
    minutes: 12,
    tag: "Admissions",
    tags: ["admissions"],
  },
  {
    id: "a-mbamission-reapply",
    title: "MBA reapplication strategies that admissions officers respect",
    source: "mbaMission Blog",
    url: "https://www.mbamission.com/blog/",
    minutes: 7,
    tag: "Admissions",
    tags: ["admissions"],
  },
  {
    id: "a-gmatclub-policy",
    title: "Latest GMAC policy changes — community breakdown",
    source: "GMAT Club Blog",
    url: "https://gmatclub.com/blog/",
    minutes: 4,
    tag: "Strategy",
    tags: ["strategy"],
  },
  {
    id: "a-manhattan-think",
    title: "How to think like the test — Manhattan Prep strategy",
    source: "Manhattan Prep Blog",
    url: "https://www.manhattanprep.com/gmat/blog/",
    minutes: 6,
    tag: "Strategy",
    tags: ["strategy", "quant", "verbal"],
  },
  {
    id: "a-stacy-essay",
    title: "School-specific essay guidance from Stacy Blackman",
    source: "Stacy Blackman Consulting",
    url: "https://www.stacyblackman.com/blog/",
    minutes: 10,
    tag: "Admissions",
    tags: ["admissions"],
  },
];

export const WEBSITES: CuratedLink[] = [
  {
    id: "mba-com",
    name: "mba.com (Official GMAC)",
    host: "mba.com",
    url: "https://www.mba.com/exams/gmat",
    description: "Test registration, official practice exams, score policy.",
    category: "official",
    tags: ["strategy"],
  },
  {
    id: "gmatclub",
    name: "GMAT Club",
    host: "gmatclub.com",
    url: "https://gmatclub.com",
    description: "1.5M+ members, adaptive tests, error log, course reviews.",
    category: "forum",
    tags: ["strategy", "quant", "verbal", "di"],
  },
  {
    id: "beatgmat",
    name: "Beat The GMAT",
    host: "beatthegmat.com",
    url: "https://www.beatthegmat.com",
    description: "15+ years of verified reviews and MBA program database.",
    category: "forum",
    tags: ["admissions", "strategy"],
  },
  {
    id: "poetsandquants",
    name: "Poets & Quants",
    host: "poetsandquants.com",
    url: "https://poetsandquants.com",
    description: "MBA news, school profiles, employment outcomes.",
    category: "admissions",
    tags: ["admissions"],
  },
  {
    id: "mbamission",
    name: "mbaMission",
    host: "mbamission.com",
    url: "https://www.mbamission.com",
    description: "Essays, recommendations, interview deep dives.",
    category: "admissions",
    tags: ["admissions"],
  },
  {
    id: "stacyblackman",
    name: "Stacy Blackman Consulting",
    host: "stacyblackman.com",
    url: "https://www.stacyblackman.com",
    description: "School-specific essay guidance and deadline tracking.",
    category: "admissions",
    tags: ["admissions"],
  },
  {
    id: "fortuna",
    name: "Fortuna Admissions",
    host: "fortunaadmissions.com",
    url: "https://fortunaadmissions.com",
    description: "Former admissions directors writing candid posts.",
    category: "admissions",
    tags: ["admissions"],
  },
];

export const COMMUNITY_THREADS: CommunityThread[] = [
  {
    id: "c-reddit-di",
    source: "r/GMAT",
    title: "Anyone else find DI harder than the official guide?",
    url: "https://www.reddit.com/r/GMAT/",
    replies: 47,
    tags: ["di"],
  },
  {
    id: "c-gmatclub-plateau",
    source: "GMAT Club",
    title: "Score plateau at 680 — what worked for you?",
    url: "https://gmatclub.com/forum/",
    replies: 122,
    tags: ["strategy", "mindset"],
  },
  {
    id: "c-reddit-pacing",
    source: "r/GMAT",
    title: "Two-week countdown — pacing tips that actually help",
    url: "https://www.reddit.com/r/GMAT/",
    replies: 38,
    tags: ["mindset", "strategy"],
  },
  {
    id: "c-mba-compare",
    source: "r/MBA",
    title: "Wharton vs Booth — how are you deciding?",
    url: "https://www.reddit.com/r/MBA/",
    replies: 89,
    tags: ["admissions"],
  },
  {
    id: "c-reddit-cr",
    source: "r/GMAT",
    title: "CR weaken questions — I keep picking the conclusion restatement",
    url: "https://www.reddit.com/r/GMAT/",
    replies: 56,
    tags: ["verbal", "cr"],
  },
];

export const INSTAGRAM_ACCOUNTS: SocialAccount[] = [
  {
    id: "ig-official",
    platform: "instagram",
    handle: "@theofficialgmat",
    followers: "~60K",
    description: "Official tips and score release announcements.",
    url: "https://www.instagram.com/theofficialgmat/",
  },
  {
    id: "ig-gmac",
    platform: "instagram",
    handle: "@gmatbygmac",
    followers: "56K",
    description: "Daily encouragement and B-school journey content.",
    url: "https://www.instagram.com/gmatbygmac/",
  },
  {
    id: "ig-club",
    platform: "instagram",
    handle: "@gmat_club",
    followers: "34K",
    description: "Question of the day — 1M+ test-takers community.",
    url: "https://www.instagram.com/gmat_club/",
  },
  {
    id: "ig-gmatpoint",
    platform: "instagram",
    handle: "@gmatpoint",
    description: "785 scorer — daily targets and tricks.",
    url: "https://www.instagram.com/gmatpoint/",
  },
  {
    id: "ig-egmat",
    platform: "instagram",
    handle: "@egmat_gmatprep",
    description: "Strong DI and Verbal explainer reels.",
    url: "https://www.instagram.com/egmat_gmatprep/",
  },
  {
    id: "ig-magoosh",
    platform: "instagram",
    handle: "@magooshgmat",
    description: "Bite-size tips, vocabulary, formulas.",
    url: "https://www.instagram.com/magooshgmat/",
  },
];

export const LINKEDIN_RESOURCES: SocialAccount[] = [
  {
    id: "li-official",
    platform: "linkedin",
    handle: "Official GMAT",
    followers: "5.8K+",
    description: "GMAC showcase — prep insights and policy updates.",
    url: "https://www.linkedin.com/showcase/officialgmat/",
  },
  {
    id: "li-club",
    platform: "linkedin",
    handle: "GMAT Club",
    followers: "16K+",
    description: "Central hub for applicants and online MBA programs.",
    url: "https://www.linkedin.com/company/gmat-club/",
  },
  {
    id: "li-poq",
    platform: "linkedin",
    handle: "Poets & Quants",
    description: "MBA admissions news and rankings.",
    url: "https://www.linkedin.com/company/poets-quants/",
  },
];

export const REDDIT_COMMUNITIES: CuratedLink[] = [
  {
    id: "r-gmat",
    name: "r/GMAT",
    host: "reddit.com",
    url: "https://www.reddit.com/r/GMAT/",
    description: "Real-time test experiences, debriefs, score reports.",
    category: "social",
    tags: ["strategy", "quant", "verbal", "di"],
  },
  {
    id: "r-mba",
    name: "r/MBA",
    host: "reddit.com",
    url: "https://www.reddit.com/r/MBA/",
    description: "Admissions, school comparisons, R1/R2 discussion.",
    category: "social",
    tags: ["admissions"],
  },
  {
    id: "r-indianmba",
    name: "r/IndianMBA",
    host: "reddit.com",
    url: "https://www.reddit.com/r/IndianMBA/",
    description: "ISB, IIM-A, and India-focused MBA paths.",
    category: "social",
    tags: ["admissions"],
  },
  {
    id: "r-europemba",
    name: "r/EuropeMBA",
    host: "reddit.com",
    url: "https://www.reddit.com/r/EuropeMBA/",
    description: "INSEAD, LBS, IESE focused discussion.",
    category: "social",
    tags: ["admissions"],
  },
];

export const PODCASTS: PodcastShow[] = [
  {
    id: "pod-poq",
    name: "Business Casual",
    publisher: "Poets & Quants",
    description: "MBA admissions news weekly.",
    url: "https://poetsandquants.com/",
  },
  {
    id: "pod-insider",
    name: "The MBA Insider Podcast",
    publisher: "Various",
    description: "Interviews with admits and admissions officers.",
    url: "https://www.mbainsider.com/",
  },
  {
    id: "pod-bschooled",
    name: "B-Schooled",
    publisher: "Stacy Blackman",
    description: "Application strategy episodes.",
    url: "https://www.stacyblackman.com/blog/category/podcast/",
  },
  {
    id: "pod-manhattan",
    name: "GMAT Strategy Podcast",
    publisher: "Manhattan Prep",
    description: "Test strategy and mindset.",
    url: "https://www.manhattanprep.com/gmat/",
  },
];

export const OFFICIAL_GMAC: CuratedLink[] = [
  {
    id: "og-guide",
    name: "GMAT Official Guide 2025–2026",
    host: "mba.com",
    url: "https://www.mba.com/exams/gmat-exam/gmat-official-guide",
    description: "Authoritative practice book aligned with Focus Edition.",
    category: "official",
    tags: ["strategy"],
  },
  {
    id: "og-mocks",
    name: "GMAT Official Practice Exams",
    host: "mba.com",
    url: "https://www.mba.com/exams/gmat-exam/gmat-official-practice",
    description: "6 full mocks from GMAC — essential before test day.",
    category: "official",
    tags: ["strategy"],
  },
  {
    id: "og-qotd",
    name: "Practice Question of the Day",
    host: "mba.com",
    url: "https://www.mba.com/exams/gmat-exam/gmat-official-starter-kit",
    description: "Free official questions to build daily habit.",
    category: "official",
    tags: ["strategy"],
  },
];

/** Map topic browser IDs → resource tags for deep-dive panels */
export const TOPIC_RESOURCE_TAGS: Record<string, ContentTag[]> = {
  // Legacy IDs (kept for compatibility)
  "number-properties": ["quant"],
  "rates-work": ["quant"],
  algebra: ["quant"],
  geometry: ["quant"],
  "word-problems": ["quant"],
  statistics: ["quant"],
  "cr-strengthen": ["verbal", "cr"],
  "cr-flaw": ["verbal", "cr"],
  "rc-main-idea": ["verbal", "rc"],
  "rc-inference": ["verbal", "rc"],
  "cr-paradox": ["verbal", "cr"],
  "ds-two-var": ["di", "ds"],
  "ds-yes-no": ["di", "ds"],
  msr: ["di"],
  "two-part": ["di"],
  graphics: ["di"],
  "table-analysis": ["di"],
  anxiety: ["mindset"],
  pacing: ["mindset", "strategy"],
  plateau: ["mindset", "strategy"],
  review: ["mindset", "strategy"],
};

function tagsForTopicId(topicId: string): ContentTag[] {
  if (TOPIC_RESOURCE_TAGS[topicId]) return TOPIC_RESOURCE_TAGS[topicId];
  if (topicId.startsWith("q-")) return ["quant"];
  if (topicId.startsWith("v-cr-")) return ["verbal", "cr"];
  if (topicId.startsWith("v-rc-")) return ["verbal", "rc"];
  if (topicId.startsWith("v-")) return ["verbal"];
  if (topicId.startsWith("di-ds")) return ["di", "ds"];
  if (topicId.startsWith("di-")) return ["di"];
  if (topicId.startsWith("m-")) return ["mindset", "strategy"];
  return ["strategy"];
}

export function resourcesForTopic(topicId: string): {
  videos: CuratedVideo[];
  articles: CuratedArticle[];
  threads: CommunityThread[];
} {
  const tags = tagsForTopicId(topicId);
  const match = (itemTags: ContentTag[]) => itemTags.some((t) => tags.includes(t));

  return {
    videos: CURATED_VIDEOS.filter((v) => match(v.tags)).slice(0, 3),
    articles: CURATED_ARTICLES.filter((a) => match(a.tags)).slice(0, 2),
    threads: COMMUNITY_THREADS.filter((c) => match(c.tags)).slice(0, 2),
  };
}

export const ALL_CURATED_LINKS: CuratedLink[] = [
  ...WEBSITES,
  ...REDDIT_COMMUNITIES,
  ...OFFICIAL_GMAC,
];
